import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Button } from "@/components/ui/button";
import { fileUrl } from "@/lib/api";
import { Star, Send, Sparkles, MapPin, FileSearch } from "lucide-react";

const creditPill = (score) => {
  if (!score) return "bg-slate-100 text-slate-600";
  if (score >= 700) return "bg-emerald-100 text-emerald-700";
  if (score >= 640) return "bg-amber-100 text-amber-700";
  return "bg-red-100 text-red-700";
};

function TenantAvatar({ t }) {
  if (t.photo_url) {
    return <img src={fileUrl(t.photo_url)} alt={t.name} className="w-11 h-11 rounded-full object-cover border border-border shrink-0" />;
  }
  return <span className="w-11 h-11 rounded-full bg-primary/10 text-primary grid place-items-center font-semibold shrink-0">{(t.name || "T").charAt(0)}</span>;
}

function TenantCardHeader({ t, shortlisted, onShortlist }) {
  return (
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-3 min-w-0">
        <TenantAvatar t={t} />
        <div className="min-w-0">
          <div className="font-display font-semibold flex items-center gap-2">
            {t.name} {t.boosted && <Sparkles className="w-4 h-4 text-amber-500" />}
          </div>
          <VerifiedBadge date={t.verified_at} />
          {t.distance_miles != null && (
            <div className="text-xs text-primary font-medium mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.distance_miles} mi away</div>
          )}
          {t.amenity_match_pct != null && (
            <div className="text-xs mt-1 font-semibold text-emerald-700">{t.amenity_match_pct}% amenity match</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {t.credit_score && (
          <span className={`text-xs font-bold px-2 py-1 rounded-full ${creditPill(t.credit_score)}`} data-testid={`credit-pill-${t.user_id}`}>
            {t.credit_score}
          </span>
        )}
        <button data-testid={`shortlist-${t.user_id}`} onClick={() => onShortlist(t.user_id)}>
          <Star className={`w-5 h-5 ${shortlisted ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
        </button>
      </div>
    </div>
  );
}

function TenantCardDetails({ t }) {
  return (
    <>
      <div className="text-sm space-y-1 text-muted-foreground mb-4">
        <div>Budget: <span className="text-foreground font-medium">${t.budget_min}–${t.budget_max}/mo</span></div>
        <div>Areas: <span className="text-foreground">{(t.preferred_areas || []).join(", ") || "—"}</span></div>
        <div>Move-in: <span className="text-foreground">{t.move_in_date || "Flexible"}</span></div>
        <div>Income: <span className="text-foreground">${t.annual_income?.toLocaleString() || "—"}/yr · {t.employment || "—"}</span></div>
        <div>Household {t.household_size} · Pets {t.pets}</div>
        {(t.min_bedrooms > 0 || t.min_bathrooms > 0) && (
          <div>Wants: <span className="text-foreground">{t.min_bedrooms || 0}+ bed · {t.min_bathrooms || 0}+ bath</span></div>
        )}
      </div>
      {t.desired_amenities?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {t.desired_amenities.slice(0, 5).map((a) => (<span key={a} className="text-[10px] bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full">{a}</span>))}
          {t.desired_amenities.length > 5 && <span className="text-[10px] text-muted-foreground">+{t.desired_amenities.length - 5}</span>}
        </div>
      )}
      {t.bio && <p className="text-sm mb-4 line-clamp-2">{t.bio}</p>}
    </>
  );
}

export function TenantCard({ t, onOffer, onShortlist, shortlisted, onViewReport }) {
  return (
    <div className="bg-white border border-border rounded-sm p-6" data-testid={`tenant-card-${t.user_id}`}>
      <TenantCardHeader t={t} shortlisted={shortlisted} onShortlist={onShortlist} />
      <TenantCardDetails t={t} />
      <div className="grid grid-cols-2 gap-2">
        <Button size="sm" variant="outline" data-testid={`view-report-${t.user_id}`} onClick={() => onViewReport?.(t)}><FileSearch className="w-4 h-4 mr-1" /> Report</Button>
        <Button size="sm" data-testid={`send-offer-${t.user_id}`} onClick={() => onOffer(t)}><Send className="w-4 h-4 mr-1" /> Send offer</Button>
      </div>
    </div>
  );
}
